/**
 * Product Sticky Add to Cart
 * Shows a sticky bar at the top when user scrolls past the main product form.
 *
 * Two operating modes:
 *   PDP mode     — scope is a [id^="productTemplate--"] section; trigger is the
 *                  product form scrolling out of view.
 *   Page mode    — scope is a [id^="productStickyAtc--"] section with a
 *                  data-trigger-selector attribute pointing to a hero CTA;
 *                  sticky shows when that CTA is out of viewport and loads
 *                  the product via fetch using data-product-handle on the trigger.
 *
 * Compare-price logic uses the inlined product JSON as source of truth to avoid
 * false negatives when [data-compare-price] is transiently empty during theme
 * variant transitions (e.g. Recharge initialisation).
 */

class ProductStickyATC {
  constructor(scope = document) {
    this.scope = scope;
    this.stickyBar = scope.querySelector('.prodcut-sticky-atc') || document.querySelector('.prodcut-sticky-atc');
    if (!this.stickyBar) return;

    this.productForm = scope.querySelector('product-form');
    this.mainForm = scope.querySelector('form[action*="/cart/add"]') || scope.querySelector('form[id^="AddToCartForm"]');
    this.variantSelectsElement = scope.querySelector('variant-selects');

    // Page mode: optional trigger element configured via data-trigger-selector.
    const triggerSelector = scope.dataset ? scope.dataset.triggerSelector : '';
    this.triggerElement = null;
    if (triggerSelector) {
      try { this.triggerElement = document.querySelector(triggerSelector); } catch (e) {}
    }

    // Page mode: optional "show immediately on mobile" override, configured via
    // data-show-immediately-mobile. When enabled, skips the trigger-element check
    // on mobile viewports so the bar is visible the instant the page loads.
    this.showImmediateMobile = scope.dataset && scope.dataset.showImmediatelyMobile === 'true';

    this.variantInputs = this.stickyBar.querySelectorAll('.sticky-variant-button input');
    this.variantListItems = this.stickyBar.querySelectorAll('.hide--desktop ul li');
    this.atcButton = this.stickyBar.querySelector('.sticky-atc-btn');
    this.atcButtonMobile = this.stickyBar.querySelector('.sticky-atc-trigger-mobile');
    this.priceElement = this.stickyBar.querySelector('.sticky-price-regular');
    this.comparePriceElement = this.stickyBar.querySelector('.sticky-price-compare');
    this.priceElementMobile = this.stickyBar.querySelector('.hide--desktop .sticky-price-regular');
    this.comparePriceElementMobile = this.stickyBar.querySelector('.hide--desktop .sticky-price-compare');
    this.priceObserver = null;

    // Parse inlined product JSON as source of truth for compare-price logic.
    // Prevents false negatives when [data-compare-price] is transiently empty
    // during theme variant transitions (e.g. Recharge initialisation).
    const productScript = scope.querySelector('script[data-product-json]')
      || document.querySelector('script[data-product-json]');
    this.productData = null;
    if (productScript) {
      try { this.productData = JSON.parse(productScript.textContent); } catch (e) {}
    }

    // Handle variant selection for desktop
    this.variantInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleVariantChange(e));
    });

    // Handle variant selection for mobile
    this.variantListItems.forEach(item => {
      item.addEventListener('click', (e) => {
        if (e.isTrusted) this._lastUserClickAt = performance.now();
        this.handleMobileVariantChange(e);
      });
    });

    this.init();
  }

  init() {
    // Show/hide sticky on scroll
    this.handleScroll();
    window.addEventListener('scroll', () => this.handleScroll());

    // Handle ATC click
    if (this.atcButton) {
      this.atcButton.addEventListener('click', (e) => {
        if (e.isTrusted) this._lastUserClickAt = performance.now();
        this.handleATCClick();
      });
    }

    if (this.atcButtonMobile) {
      this.atcButtonMobile.addEventListener('click', (e) => {
        if (e.isTrusted) this._lastUserClickAt = performance.now();
        this.handleATCClickMobile();
      });
    }

    // Subscribe to variant changes from main form using pub/sub system
    if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
      this.variantChangeUnsubscriber = window.subscribe(
        window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
        (data) => {
          setTimeout(() => {
            this.handleMainFormVariantChange(data);
          }, 2000);
        }
      );
    }

    // Observe price changes in the main form
    this.observePriceChanges();

    this.syncPreorderState();

    // Page mode: sync product data (title, price, variant id) from the
    // trigger element's data attributes so the sticky reflects the right product.
    this.syncFromTriggerElement();
  }

  // ---------------------------------------------------------------------------
  // Page mode helpers
  // ---------------------------------------------------------------------------

  syncFromTriggerElement() {
    if (!this.triggerElement) return;

    const variantId = this.triggerElement.getAttribute('data-variant-id');
    const productHandle = this.triggerElement.getAttribute('data-product-handle');

    if (variantId) {
      const hiddenVariantInput = this.scope.querySelector('input[name="id"][data-product-select]');
      if (hiddenVariantInput) hiddenVariantInput.value = variantId;
    }

    if (!productHandle) return;

    // Skip fetch when the pre-rendered product already matches.
    const preRendered = this.scope.querySelector('script[data-product-json]');
    if (preRendered) {
      try {
        const data = JSON.parse(preRendered.textContent);
        if (data && data.handle === productHandle) return;
      } catch (e) {}
    }

    fetch(`/products/${productHandle}.js`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(product => {
        if (!product) return;
        this.applyProductData(product, variantId);
      })
      .catch(() => {});
  }

  applyProductData(product, variantId) {
    const variant = (variantId && product.variants.find(v => String(v.id) === String(variantId)))
      || product.variants.find(v => v.available)
      || product.variants[0];
    if (!variant) return;

    // Replace stored product JSON so other code paths (variant lookups) use the new data.
    const preRendered = this.scope.querySelector('script[data-product-json]');
    if (preRendered) {
      preRendered.textContent = JSON.stringify(product);
      try { this.productData = product; } catch (e) {}
    }

    // Title
    const titleEl = this.stickyBar.querySelector('.sticky-title');
    if (titleEl) titleEl.textContent = product.title;

    // Prices
    const formatMoney = (cents) => {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      return `$${(cents / 100).toFixed(2)}`;
    };

    const priceText = formatMoney(variant.price);
    if (this.priceElement) this.priceElement.textContent = priceText;
    if (this.priceElementMobile) this.priceElementMobile.textContent = priceText;

    const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    const compareText = showCompare ? formatMoney(variant.compare_at_price) : '';
    [this.comparePriceElement, this.comparePriceElementMobile].forEach(el => {
      if (!el) return;
      if (showCompare) {
        el.textContent = compareText;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });

    // Sync hidden form variant id
    const hiddenVariantInput = this.scope.querySelector('input[name="id"][data-product-select]');
    if (hiddenVariantInput) hiddenVariantInput.value = variant.id;
  }

  // ---------------------------------------------------------------------------
  // Scroll
  // ---------------------------------------------------------------------------

  handleScroll() {
    // Page mode override: if configured, show immediately on mobile viewports
    // without waiting for the trigger element to scroll out of view.
    if (this.showImmediateMobile && window.theme?.isMobile?.()) {
      this.stickyBar.classList.add('visible');
      return;
    }

    // Page mode: show/hide based on whether the configured trigger element
    // (e.g. the hero CTA) is out of the viewport.
    if (this.triggerElement) {
      const rect = this.triggerElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const outOfViewport = rect.bottom < 0 || rect.top > viewportHeight;

      if (outOfViewport) {
        this.stickyBar.classList.add('visible');
      } else {
        this.stickyBar.classList.remove('visible');
      }
      return;
    }

    // PDP mode: show when the product form has scrolled above the viewport.
    const targetElement = this.mainForm || this.productForm || this.variantSelectsElement;
    if (!targetElement) return;

    const formRect = targetElement.getBoundingClientRect();

    if (formRect.bottom < 0) {
      this.stickyBar.classList.add('visible');
    } else {
      this.stickyBar.classList.remove('visible');
    }
  }

  // ---------------------------------------------------------------------------
  // Variant handling
  // ---------------------------------------------------------------------------

  handleVariantChange(e) {
    const input = e.currentTarget;
    if (input.disabled) return;
    const allVariantButtons = this.stickyBar.querySelectorAll('.sticky-variant-button');
    allVariantButtons.forEach(button => button.classList.remove('is-active'));

    const parentLabel = input.closest('.sticky-variant-button');
    if (parentLabel) {
      parentLabel.classList.add('is-active');
    }

    // Update price
    const price = input.dataset.price;
    if (this.priceElement && price) {
      this.priceElement.textContent = price;
    }

    const comparePrice = input.dataset.comparePrice;
    if (comparePrice !== undefined) {
      [this.comparePriceElement, this.comparePriceElementMobile].forEach((el) => {
        if (!el) return;
        el.textContent = comparePrice;
        el.style.display = comparePrice ? '' : 'none';
      });
    }

    // Trigger variant change on main form using variant ID
    const variantId = input.dataset.variantId;
    this.selectMainFormVariant(variantId);
  }

  handleMobileVariantChange(e) {
    const listItem = e.currentTarget;

    if (listItem.classList.contains('disabled')) return;

    const variantTitle = listItem.getAttribute('data-value');

    // Resolve variant id from the section's inlined product JSON so we can
    // update the hidden form directly without waiting on <variant-selects>.
    let variantId = null;
    const productScript = this.scope.querySelector('script[data-product-json]');
    if (productScript) {
      try {
        const product = JSON.parse(productScript.textContent);
        const match = product.variants.find(v => v.title === variantTitle);
        if (match) variantId = match.id;
      } catch (err) {}
    }

    // Page mode: set the section's hidden form variant id directly so we
    // don't depend on <variant-selects> (which only exists on PDP).
    if (variantId) {
      const hiddenVariantInput = this.scope.querySelector('input[name="id"][data-product-select]')
        || (this.mainForm || this.productForm)?.querySelector('input[name="id"]');
      if (hiddenVariantInput) hiddenVariantInput.value = variantId;
    }

    // PDP mode: also update <variant-selects> radios so the main form stays in sync.
    const container = this.variantSelectsElement;
    if (container) {
      const variantInputs = container.querySelectorAll('input[type="radio"][name*="option"]');
      variantInputs.forEach(input => {
        if (input.value === variantTitle) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Close the mobile variant selector
    if (this.atcButtonMobile) {
      this.atcButtonMobile.classList.remove('active');
    }

    // Submit on the next frame so the input value commit lands first.
    requestAnimationFrame(() => this.handleATCClick());
  }

  // ---------------------------------------------------------------------------
  // ATC
  // ---------------------------------------------------------------------------

  handleATCClick() {
    const container = this.mainForm || this.productForm;
    const mainATCButton = container?.querySelector('[name="add"]');
    if (!mainATCButton) return;

    mainATCButton.click();

    // When the native <cart-drawer> isn't on the page (e.g. page templates), the
    // theme's theme:cart:add chain can't open it. Rebuy intercepts /cart/add.js
    // but won't auto-open without a direct call — trigger it ourselves.
    // Guard: only fire if the user clicked the sticky bar within the last 1 s,
    // so a stray programmatic call to handleATCClick can never auto-open the cart.
    const recentClick = this._lastUserClickAt && (performance.now() - this._lastUserClickAt) < 1000;
    if (recentClick && !document.querySelector('cart-drawer') && typeof window.Rebuy?.SmartCart?.show === 'function') {
      requestAnimationFrame(() => {
        try { window.Rebuy.SmartCart.show(); } catch (e) {}
      });
    }
  }

  handleATCClickMobile() {
    const hasVariants = this.atcButtonMobile.dataset.variant === 'true';
    const isSoldout = this.atcButtonMobile.classList.contains('soldout');

    if (isSoldout) return;

    if (hasVariants) {
      this.atcButtonMobile.classList.toggle('active');
      return;
    }

    const container = this.mainForm || this.productForm;
    const mainATCButton = container?.querySelector('[name="add"]');
    if (mainATCButton) {
      setTimeout(() => {
        mainATCButton.click();
      }, 200);
    }
  }

  // ---------------------------------------------------------------------------
  // Main form variant sync
  // ---------------------------------------------------------------------------

  selectMainFormVariant(variantId) {
    if (!(this.mainForm || this.productForm) || !variantId) return;

    window.__stickyBarVariantChange = true;

    if (this._stickyFlagTimeout) clearTimeout(this._stickyFlagTimeout);
    this._stickyFlagTimeout = setTimeout(() => {
      window.__stickyBarVariantChange = false;
    }, 1000);

    const formContainer = this.mainForm || this.productForm;

    const variantSelect = formContainer.querySelector('select[name="id"]') || formContainer.querySelector('input[name="id"]');
    if (variantSelect) {
      if (variantSelect.tagName === 'SELECT' || (variantSelect.tagName === 'INPUT' && variantSelect.type === 'hidden')) {
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Method 2: Use product JSON to map ID → Option Values
    let productScript = document.querySelector(`script[data-product-id]`);
    if (!productScript) {
      productScript = document.querySelector(`script[type="application/json"][data-product-json]`);
    }

    if (productScript) {
      try {
        const productData = JSON.parse(productScript.textContent);
        const variant = productData.variants.find(v => v.id == variantId);

        if (variant && variant.options) {
          const currentVariantSelects = this.scope.querySelector('variant-selects');
          const currentProductForm = this.scope.querySelector('product-form');
          const inputContainer = currentVariantSelects || currentProductForm || this.variantSelectsElement || this.productForm;

          const selectedOptionValueIds = [];
          let firstInput = null;

          variant.options.forEach((val, i) => {
            const optionIndex = "option" + (i + 1);
            const safeValue = val.replace(/"/g, '\\"');

            let input = inputContainer.querySelector(`input[data-index="${optionIndex}"][value="${safeValue}"]`);

            if (!input) {
              const hiddenInput = inputContainer.querySelector(`input[type="hidden"][data-index="${optionIndex}"]`);
              if (hiddenInput) {
                hiddenInput.value = val;
                input = hiddenInput;

                const popout = hiddenInput.closest('popout-select');
                if (popout) {
                  const triggerText = popout.querySelector('[data-popout-text]');
                  if (triggerText) triggerText.textContent = val;
                  popout.querySelectorAll('.select-popout__item').forEach(li => {
                    li.classList.toggle('select-popout__item--current', li.getAttribute('data-value') === val);
                  });
                }
              }
            }

            if (!input) {
              input = inputContainer.querySelector(`input[value="${safeValue}"]`);
            }

            if (input) {
              if (input.type === 'radio' || input.type === 'checkbox') {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }

              const label = inputContainer.querySelector(`label[for="${input.id}"]`);
              if (label) {
                const name = input.name;
                inputContainer.querySelectorAll(`input[name="${name}"]`).forEach(sibling => {
                  const sibLabel = inputContainer.querySelector(`label[for="${sibling.id}"]`);
                  if (sibLabel) sibLabel.classList.remove('is-active', 'selected');
                });
                label.classList.add('is-active', 'selected');
              }

              input.dispatchEvent(new Event('change', { bubbles: true }));

              if (input.dataset.optionValueId) {
                selectedOptionValueIds.push(input.dataset.optionValueId);
              }
              if (!firstInput) firstInput = input;
            }
          });

          if (window.theme?.PUB_SUB_EVENTS?.optionValueSelectionChange && window.publish && selectedOptionValueIds.length > 0) {
            window.publish(window.theme.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event: new Event('change'),
                target: firstInput || inputContainer,
                selectedOptionValues: selectedOptionValueIds
              }
            });
          }

          return;
        }
      } catch (e) {
        console.warn("[ProductStickyATC] Error parsing product JSON", e);
      }
    }

    // Method 3: Fallback — text matching for legacy/simple products
    const stickyInput = this.stickyBar.querySelector(`input[data-variant-id="${variantId}"]`);
    if (stickyInput) {
      const parentLabel = stickyInput.closest('.sticky-variant-button');
      if (parentLabel) {
        const optionText = parentLabel.textContent.trim();
        const inputContainer = this.variantSelectsElement || this.productForm;
        if (inputContainer) {
          const optionInputs = inputContainer.querySelectorAll('input[data-single-option-selector]');
          optionInputs.forEach(input => {
            if (input.value === optionText) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      }
    }
  }

  handleMainFormVariantChange(data) {
    const formContainer = this.mainForm || this.productForm;
    if (!formContainer) return;

    const variantIdInput = formContainer.querySelector('[name="id"]');
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    setTimeout(() => {
      this.updateStickyVariant(variantId);
    }, 2000);
  }

  updateStickyVariant(variantId) {
    const allVariantButtons = this.stickyBar.querySelectorAll('.sticky-variant-button');
    allVariantButtons.forEach(button => button.classList.remove('is-active'));

    this.variantInputs.forEach(input => {
      if (input.dataset.variantId === String(variantId)) {
        input.checked = true;

        const parentLabel = input.closest('.sticky-variant-button');
        if (parentLabel) {
          parentLabel.classList.add('is-active');
        }
      }
    });

    this.updatePricesFromMainForm();
    this.syncPreorderState();
  }

  // ---------------------------------------------------------------------------
  // Preorder state
  // ---------------------------------------------------------------------------

  syncPreorderState() {
    const formContainer = this.mainForm || this.productForm;
    const mainATCButton = formContainer?.querySelector('[name="add"]');
    if (!mainATCButton) return;

    const isPreorder = mainATCButton.classList.contains('PreOrder-Btn');

    const sourceTextSpan = mainATCButton.querySelector('[data-add-to-cart-text]');
    let sourceText = "";

    if (sourceTextSpan) {
      sourceTextSpan.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          sourceText += node.textContent;
        }
      });
    } else {
      const priceClone = mainATCButton.cloneNode(true);
      const price = priceClone.querySelector('[data-product-price], .price, .money');
      if (price) price.remove();
      sourceText = priceClone.textContent;
    }

    sourceText = sourceText.trim();

    const buttons = [this.atcButton, this.atcButtonMobile].filter(Boolean);

    buttons.forEach(btn => {
      if (isPreorder) btn.classList.add('PreOrder-Btn');
      else btn.classList.remove('PreOrder-Btn');

      if (btn.classList.contains('sticky-atc-trigger-mobile')) return;

      const targetTextContainer = btn.querySelector('.btn-text') || btn.querySelector('.add-text') || btn;

      let textUpdated = false;
      targetTextContainer.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
          node.textContent = sourceText + ' ';
          textUpdated = true;
        }
      });

      if (!textUpdated) {
        if (targetTextContainer.firstChild) {
          const textNode = document.createTextNode(sourceText + ' ');
          targetTextContainer.insertBefore(textNode, targetTextContainer.firstChild);
        } else {
          targetTextContainer.textContent = sourceText;
        }
      }
    });

    const preorderInput = this.mainForm?.querySelector('.preorder-property') || this.productForm?.querySelector('.preorder-property');
    if (preorderInput) {
      if (isPreorder) {
        preorderInput.removeAttribute('disabled');
      } else {
        preorderInput.setAttribute('disabled', 'disabled');
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Price observer
  // ---------------------------------------------------------------------------

  observePriceChanges() {
    const initObserver = () => {
      const priceContainer = document.querySelector('[data-add-to-cart-text]');

      if (!priceContainer) {
        return false;
      }

      this.priceObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const rechargePrice = priceContainer.querySelector('.recharge-inner-most-price');
            const regularPrice = priceContainer.querySelector('.product__price--regular.pdp-product');

            if (rechargePrice || regularPrice) {
              this.updatePricesFromMainForm();
            }
          }
        });
      });

      this.priceObserver.observe(priceContainer, {
        childList: true,
        characterData: true,
        subtree: true
      });

      return true;
    };

    let attempts = 0;
    const maxAttempts = 10;
    const retryInterval = 500;

    const tryInit = () => {
      attempts++;
      if (initObserver()) return;
      if (attempts < maxAttempts) {
        setTimeout(tryInit, retryInterval);
      }
    };

    setTimeout(tryInit, 1000);
  }

  updatePricesFromMainForm() {
    // Regular price: Recharge first, then standard PDP selector.
    let mainPrice = document.querySelector('.recharge-inner-most-price');
    if (!mainPrice) mainPrice = document.querySelector('.product__price--regular.pdp-product');
    if (mainPrice) {
      const priceText = mainPrice.textContent.trim();
      if (this.priceElement) this.priceElement.textContent = priceText;
      if (this.priceElementMobile) this.priceElementMobile.textContent = priceText;
    }

    // Compare price: use inlined product JSON as source of truth (three-state):
    //   true  → JSON confirms compare price exists  → show
    //   false → JSON confirms no compare price      → hide
    //   null  → no reliable JSON data               → preserve current state (Liquid render wins)
    const currentVariantId = (this.mainForm || this.productForm)
      ?.querySelector('[name="id"]')?.value;
    let variantHasComparePrice = null;

    if (currentVariantId && this.productData) {
      const variant = this.productData.variants
        ?.find(v => String(v.id) === String(currentVariantId));
      if (variant) {
        variantHasComparePrice = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      }
    }

    if (document.querySelector('[data-product-price][data-promo-percent]')) {
      variantHasComparePrice = true;
    }

    const mainComparePriceEl = document.querySelector('[data-compare-price]');
    const comparePriceText = mainComparePriceEl?.textContent.trim();

    if (variantHasComparePrice === true) {
      if (this.comparePriceElement) {
        if (comparePriceText) this.comparePriceElement.textContent = comparePriceText;
        this.comparePriceElement.style.display = '';
      }
      if (this.comparePriceElementMobile) {
        if (comparePriceText) this.comparePriceElementMobile.textContent = comparePriceText;
        this.comparePriceElementMobile.style.display = '';
      }
    } else if (variantHasComparePrice === false) {
      if (this.comparePriceElement) this.comparePriceElement.style.display = 'none';
      if (this.comparePriceElementMobile) this.comparePriceElementMobile.style.display = 'none';
    } else if (comparePriceText) {
      if (this.comparePriceElement) {
        this.comparePriceElement.textContent = comparePriceText;
        this.comparePriceElement.style.display = '';
      }
      if (this.comparePriceElementMobile) {
        this.comparePriceElementMobile.textContent = comparePriceText;
        this.comparePriceElementMobile.style.display = '';
      }
    }
    // variantHasComparePrice === null AND comparePriceText is empty:
    // no reliable data — preserve current state; Liquid initial render is source of truth.
  }
}

// Expose class globally
window.ProductStickyATC = ProductStickyATC;

const initStickyATC = () => {
  // [id^="productTemplate--"]  → PDP sections
  // [id^="productStickyAtc--"] → Page-context sections (admin-customizable, per-page)
  const sections = document.querySelectorAll('[id^="productTemplate--"], [id^="productStickyAtc--"]');
  if (sections.length > 0) {
    sections.forEach(section => {
      if (!section.dataset.stickyAtcInitialized) {
        new ProductStickyATC(section);
        section.dataset.stickyAtcInitialized = 'true';
      }
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyATC);
} else {
  initStickyATC();
}

setTimeout(initStickyATC, 500);
setTimeout(initStickyATC, 1500);